import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const fmt = (v) => v != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v) : '—';
const fmtPct = (v) => v != null ? `${(v * 100).toFixed(1)} %` : '—';

const TYPE_COLORS = {
  ACTIF:    { bg: '#e8f5e9', color: '#2E7D32', label: 'Actif' },
  PASSIF:   { bg: '#e3f2fd', color: '#1565C0', label: 'Passif' },
  CHARGE:   { bg: '#fce4ec', color: '#C62828', label: 'Charge' },
  PRODUIT:  { bg: '#f3e5f5', color: '#6A1B9A', label: 'Produit' },
};

function ScoreDetail({ label, value, weight }) {
  const pct = value != null ? value.toFixed(1) : '—';
  const color = value >= weight * 0.8 ? 'var(--ecf-success)' : value >= weight * 0.5 ? 'var(--ecf-warning)' : 'var(--ecf-error)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed var(--ecf-mid-grey)', fontSize: '0.82rem' }}>
      <span style={{ color: 'var(--ecf-grey)' }}>{label} <span style={{ color: '#bbb' }}>({weight}pts)</span></span>
      <span style={{ fontWeight: 700, color }}>{pct}</span>
    </div>
  );
}

function PosteRow({ poste, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [mn, setMn] = useState(poste.montant_n);
  const [mn1, setMn1] = useState(poste.montant_n1 || '');
  const t = TYPE_COLORS[poste.type_poste] || { bg: '#f4f4f4', color: '#666', label: poste.type_poste };

  const save = async () => {
    await onSave(poste.id, { montant_n: parseFloat(mn), montant_n1: mn1 !== '' ? parseFloat(mn1) : null });
    setEditing(false);
  };

  return (
    <tr style={{ background: editing ? '#fffde7' : '' }}>
      <td><code style={{ fontSize: '0.78rem', background: '#f4f4f4', padding: '1px 5px' }}>{poste.numero_compte}</code></td>
      <td style={{ fontSize: '0.82rem', color: 'var(--ecf-grey)' }}>{poste.libelle_normalise}</td>
      <td><span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', background: t.bg, color: t.color }}>{t.label}</span></td>
      <td style={{ textAlign: 'right', fontWeight: 600 }}>
        {editing
          ? <input type="number" value={mn} onChange={e => setMn(e.target.value)} style={{ width: 120, textAlign: 'right', padding: '2px 6px', border: '1px solid var(--ecf-red)' }} />
          : fmt(poste.montant_n)}
      </td>
      <td style={{ textAlign: 'right', color: 'var(--ecf-grey)' }}>
        {editing
          ? <input type="number" value={mn1} onChange={e => setMn1(e.target.value)} placeholder="N-1" style={{ width: 120, textAlign: 'right', padding: '2px 6px', border: '1px solid var(--ecf-mid-grey)' }} />
          : fmt(poste.montant_n1)}
      </td>
      <td><span style={{ fontSize: '0.65rem', color: poste.source === 'IA' ? 'var(--ecf-red)' : 'var(--ecf-success)', fontWeight: 700 }}>{poste.source}</span></td>
      {canEdit && (
        <td>
          {editing
            ? <><button className="btn btn-primary" style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={save}>✓</button>
               <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: '0.72rem', marginLeft: 4 }} onClick={() => setEditing(false)}>✕</button></>
            : <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={() => setEditing(true)}>Corriger</button>
          }
        </td>
      )}
    </tr>
  );
}

export default function BilanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ACTIF');
  const [validating, setValidating] = useState(false);
  const [msg, setMsg] = useState(null);

  const canEdit = user.role === 'GESTIONNAIRE' || user.role === 'ADMIN';

  useEffect(() => { loadDetail(); }, [id]);

  const loadDetail = async () => {
    try {
      const { data } = await api.get(`/bilans/${id}/detail`);
      setDetail(data);
    } catch (e) {
      setMsg({ type: 'error', text: 'Impossible de charger le bilan' });
    } finally { setLoading(false); }
  };

  const handleSavePoste = async (posteId, updates) => {
    await api.put(`/bilans/${id}/postes/${posteId}`, updates);
    await loadDetail();
    setMsg({ type: 'success', text: 'Poste corrigé — indicateurs recalculés.' });
    setTimeout(() => setMsg(null), 3000);
  };

  const handleValider = async () => {
    setValidating(true);
    try {
      await api.post(`/bilans/${id}/valider`);
      await loadDetail();
      setMsg({ type: 'success', text: 'Bilan validé ✅' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' });
    } finally { setValidating(false); }
  };

  if (loading) return <div><div className="topbar"><div className="topbar-title">Détail bilan</div></div><div className="page-content"><p style={{ color: '#999' }}>Chargement...</p></div></div>;
  if (!detail) return null;

  const { bilan, postes, indicateurs: ind } = detail;
  const tabs = ['ACTIF', 'PASSIF', 'CHARGE', 'PRODUIT'];
  const postesFiltres = postes.filter(p => p.type_poste === activeTab);

  const totalActif = postes.filter(p => p.type_poste === 'ACTIF').reduce((s, p) => s + (p.montant_n || 0), 0);
  const totalPassif = postes.filter(p => p.type_poste === 'PASSIF').reduce((s, p) => s + (p.montant_n || 0), 0);
  const equilibre = Math.abs(totalActif - totalPassif) < 1;

  return (
    <div>
      <div className="topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.78rem' }} onClick={() => navigate('/bilans')}>← Retour</button>
          <div className="topbar-title">{bilan.fichier_nom} — {bilan.exercice}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {bilan.statut === 'A_VERIFIER' && canEdit && (
            <button className="btn btn-primary" onClick={handleValider} disabled={validating}>
              {validating ? '...' : '✅ Valider ce bilan'}
            </button>
          )}
          {bilan.statut === 'VALIDE' && <span className="badge badge-success" style={{ padding: '6px 12px' }}>✅ Validé</span>}
        </div>
      </div>

      <div className="page-content">
        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

        {/* Scores & Statut */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div className="card">
            <div className="card-title">Indice de confiance</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, fontFamily: 'var(--font-condensed)', color: bilan.score_confiance >= 85 ? 'var(--ecf-success)' : bilan.score_confiance >= 70 ? 'var(--ecf-warning)' : 'var(--ecf-error)' }}>
                {bilan.score_confiance?.toFixed(0)}%
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--ecf-grey)', marginBottom: 4 }}>
                  {bilan.score_confiance >= 85 ? 'Insertion automatique' : bilan.score_confiance >= 70 ? 'Vérification requise' : 'Score insuffisant'}
                </div>
                <div style={{ fontSize: '0.75rem' }}>
                  Bilan équilibré : {equilibre ? <span style={{ color: 'var(--ecf-success)', fontWeight: 700 }}>✅ Oui</span> : <span style={{ color: 'var(--ecf-error)', fontWeight: 700 }}>❌ Non</span>}
                </div>
              </div>
            </div>
            <ScoreDetail label="Concordance IA / OCR" value={bilan.score_ia_ocr} weight={40} />
            <ScoreDetail label="Codes PCG reconnus" value={bilan.score_pcg} weight={30} />
            <ScoreDetail label="Actif = Passif" value={bilan.score_equilibre} weight={20} />
            <ScoreDetail label="Structure JSON" value={bilan.score_structure} weight={10} />
          </div>

          {/* Indicateurs clés */}
          {ind && (
            <div className="card">
              <div className="card-title">Indicateurs clés</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, background: 'var(--ecf-mid-grey)' }}>
                {[
                  ['Chiffre d\'affaires', fmt(ind.chiffre_affaires)],
                  ['EBE / CA', fmtPct(ind.taux_ebe)],
                  ['Résultat net', fmt(ind.resultat_net)],
                  ['Résultat net / CA', fmtPct(ind.taux_resultat_net)],
                  ['Masse salariale / CA', fmtPct(ind.ratio_masse_salariale)],
                  ['Charges ext. / CA', fmtPct(ind.ratio_charges_externes)],
                  ['Fonds propres', fmt(ind.fonds_propres)],
                  ['Trésorerie nette', fmt(ind.tresorerie_nette)],
                  ['Ratio d\'endettement', ind.ratio_endettement != null ? ind.ratio_endettement.toFixed(2) : '—'],
                  ['CAF', fmt(ind.caf)],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: 'white', padding: '10px 14px' }}>
                    <div style={{ fontSize: '0.65rem', letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ecf-grey)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem', fontWeight: 700 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Postes comptables */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ecf-mid-grey)', display: 'flex', gap: 0 }}>
            {tabs.map(tab => {
              const t = TYPE_COLORS[tab];
              const count = postes.filter(p => p.type_poste === tab).length;
              return (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{
                  padding: '8px 20px', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-condensed)', fontWeight: 700,
                  fontSize: '0.85rem', letterSpacing: 1, textTransform: 'uppercase',
                  background: activeTab === tab ? t.color : 'transparent',
                  color: activeTab === tab ? 'white' : t.color,
                  borderBottom: activeTab === tab ? `3px solid ${t.color}` : '3px solid transparent',
                }}>
                  {t.label} <span style={{ opacity: 0.7, fontSize: '0.75rem' }}>({count})</span>
                </button>
              );
            })}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: 24, fontSize: '0.82rem' }}>
              {activeTab === 'ACTIF' && <span style={{ color: 'var(--ecf-grey)' }}>Total : <strong>{fmt(totalActif)}</strong></span>}
              {activeTab === 'PASSIF' && <span style={{ color: 'var(--ecf-grey)' }}>Total : <strong>{fmt(totalPassif)}</strong></span>}
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Compte</th><th>Libellé</th><th>Type</th>
                  <th style={{ textAlign: 'right' }}>Montant N</th>
                  <th style={{ textAlign: 'right' }}>Montant N-1</th>
                  <th>Source</th>
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {postesFiltres.length === 0
                  ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#999' }}>Aucun poste</td></tr>
                  : postesFiltres.map(p => (
                    <PosteRow key={p.id} poste={p} canEdit={canEdit} onSave={handleSavePoste} />
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
